import { convertArrayToCSV } from 'convert-array-to-csv';
import { find, get } from 'lodash';
import {
  AGGREGATE_PERCENTAGE,
  PRACTICE_CHARACTERIZATION,
  UNIT_VS
} from '../../../../constants/appraisal-report-types';
import GET_MERGED_PRACTICE_AREAS from '../../../shared/queries/getMergedPracticeAreasQuery';
import GET_MDDAP_PRACTICE_GROUPS from '../../../organizationReports/queries/getMddapPracticeGroupsQuery';
import GET_INDUSTRY_APPRAISAL_VIEW from '../../queries/getIndustryAppraisalViewReport';
import GET_ORG_REPORT from '../../queries/getOrgAppraisalReport';
import { getPercentageRangeIndex } from '../shared/services/getPercentageRangeIndex';
import { defaultAggregatePercentageModel } from '../shared/constants/DefaultAggregatePercentageModel';
import AppraisalComparisonMapper from '../shared/mapper/AppraisalComparisonMapper';

const fetchData = async (appraisalOrgUnit, appraisalProgramYear, orgUnitId) => {
  const apollo = require('../../../../apollo/configureApollo').default();

  const targetPracticeAreas = get(appraisalOrgUnit, 'AppraisalOrgUnitTargetPracticeAreas', []);

  const appraisalViewRequest = AppraisalComparisonMapper.toAppraisalViewRequests(
    targetPracticeAreas,
    appraisalProgramYear
  );

  const orgReportDataPromise = apollo.query({
    query: GET_ORG_REPORT,
    variables: {
      orgUnitId: orgUnitId,
      appraisalViewRequest: appraisalViewRequest
    }
  });

  const industryViewReportDataPromise = apollo.query({
    query: GET_INDUSTRY_APPRAISAL_VIEW,
    variables: {
      appraisalViewRequest: appraisalViewRequest
    }
  });

  const [orgReportData, industryViewReportData] = await Promise.all([
    orgReportDataPromise,
    industryViewReportDataPromise
  ]);

  if (orgReportData.error || industryViewReportData.error) {
    throw new Error(orgReportData.error || industryViewReportData.error);
  }

  return { orgReportData: orgReportData.data, industryViewReportData: industryViewReportData.data };
};

const prepareArray = async (
  isSelectPracticeCharacterization,
  isSelectAggregatePercentage,
  mddapPracticeGroups,
  isPercentageVisible,
  newPoint
) => {
  if (isSelectPracticeCharacterization) {
    let levels = [];
    mddapPracticeGroups.forEach(element => {
      if (!element.Practices.length) return;

      element.Practices.forEach(p => {
        const exist = levels.some(
          l => l.level === element.Level && l.number === p.NumberWithinLevel
        );

        if (!exist) {
          levels.push({ level: element.Level, number: p.NumberWithinLevel });
        }
      });
    });

    levels = levels.sort((a, b) => a.level - b.level);
    levels.forEach(obj => {
      const level = obj.level + '.' + obj.number;
      newPoint[`${level} Satisfied`] = null;
      newPoint[`${level} Partial`] = null;
      newPoint[`${level} Deficient`] = null;
    });
  }

  if (isPercentageVisible) {
    newPoint['Percent'] = null;
  }

  if (isSelectAggregatePercentage) {
    newPoint['0%-20%'] = null;
    newPoint['21%-40%'] = null;
    newPoint['41%-60%'] = null;
    newPoint['61%-80%'] = null;
    newPoint['81%-100%'] = null;
  }
};

const prepareData = async (
  appraisalOrgUnit,
  appraisalProgramYear,
  orgUnitId,
  selectedReports,
  isPercentageVisible,
  showPracticeCharacterizationForUnit
) => {
  const apollo = require('../../../../apollo/configureApollo').default();

  const newPoint = {
    'Program Year': null,
    Type: null,
    'Practice Area Name': null,
    'Practice Group Level': null
  };

  const practiceAreas = get(
    await apollo.query({
      query: GET_MERGED_PRACTICE_AREAS
    }),
    'data.practiceAreasMerged',
    []
  );

  const mddapPracticeGroups = get(
    await apollo.query({
      query: GET_MDDAP_PRACTICE_GROUPS
    }),
    'data.mddapPracticeGroups',
    []
  );

  if (!practiceAreas.length) throw new Error('Error retrieving practice area data');

  const isSelectUnitVs = selectedReports.some(x => x == UNIT_VS);
  const isSelectPracticeCharacterization = selectedReports.some(
    x => x == PRACTICE_CHARACTERIZATION
  );
  const isSelectAggregatePercentage = selectedReports.some(x => x == AGGREGATE_PERCENTAGE);

  prepareArray(
    isSelectPracticeCharacterization,
    isSelectAggregatePercentage,
    mddapPracticeGroups,
    isPercentageVisible,
    newPoint
  );

  const queryData = await fetchData(appraisalOrgUnit, appraisalProgramYear, orgUnitId);
  const appraisalOrgs = get(
    queryData.orgReportData,
    'organizationAppraisalReport.PracticeAreas',
    []
  );
  const appraisalIndustry = get(
    queryData.industryViewReportData,
    'appraisalComparisonView.PracticeAreas',
    []
  );

  const preparedData = [];
  for (const { MergedPracticeAreaIds, Name, Acronym } of practiceAreas) {
    const name = `${Name} (${Acronym})`;

    const unitDataPoint = { ...newPoint };
    const industryDataPoint = { ...newPoint };
    const orgDataPoint = { ...newPoint };

    const targetPracticeAreas = get(appraisalOrgUnit, 'AppraisalOrgUnitTargetPracticeAreas', []);
    const targetPracticeArea = find(targetPracticeAreas, tpa =>
      MergedPracticeAreaIds.includes(tpa.PracticeArea.Id)
    );

    const pgl = get(targetPracticeArea, 'PracticeGroup.Level', 'N/A');
    const unitPercent = get(targetPracticeArea, 'AggregatePercentage', null);

    updateDataPoint(
      `Program Year`,
      appraisalProgramYear,
      unitDataPoint,
      industryDataPoint,
      orgDataPoint
    );
    updateDataPoint('Type', 'Unit', unitDataPoint);
    updateDataPoint('Type', 'Industry', industryDataPoint);
    updateDataPoint('Type', 'Organization', orgDataPoint);
    updateDataPoint(`Practice Area Name`, name, unitDataPoint, industryDataPoint, orgDataPoint);
    updateDataPoint(`Practice Group Level`, pgl, unitDataPoint, industryDataPoint, orgDataPoint);

    const selectedPaOrg = appraisalOrgs.find(x =>
      x.Ids.some(id => MergedPracticeAreaIds.includes(id))
    );
    const selectedPaIndustry = appraisalIndustry.find(x =>
      x.Ids.some(id => MergedPracticeAreaIds.includes(id))
    );

    let totalAppraisalOrg = 0;
    let totalAppraisalIndustry = 0;
    if (targetPracticeArea) {
      totalAppraisalOrg = Math.max(
        ...selectedPaOrg.Practices.map(
          practice => practice.Satisfied + practice.Partial + practice.Deficient
        )
      );
      totalAppraisalIndustry = Math.max(
        ...selectedPaIndustry.Practices.map(
          practice => practice.Satisfied + practice.Partial + practice.Deficient
        )
      );
    }

    // Load Practice Characterization
    if (targetPracticeArea && isSelectPracticeCharacterization) {
      if (totalAppraisalOrg < 3) {
        selectedPaOrg.Practices.forEach(pra => {
          updateDataPoint(`${pra.Name} Satisfied`, `N/A`, orgDataPoint);
          updateDataPoint(`${pra.Name} Partial`, `N/A`, orgDataPoint);
          updateDataPoint(`${pra.Name} Deficient`, `N/A`, orgDataPoint);

          if (showPracticeCharacterizationForUnit) {
            updateDataPoint(`${pra.Name} Satisfied`, `N/A`, unitDataPoint);
            updateDataPoint(`${pra.Name} Partial`, `N/A`, unitDataPoint);
            updateDataPoint(`${pra.Name} Deficient`, `N/A`, unitDataPoint);
          }
        });
      } else {
        selectedPaOrg.Practices.forEach(pra => {
          updateDataPoint(`${pra.Name} Satisfied`, pra.Satisfied || 0, orgDataPoint);
          updateDataPoint(`${pra.Name} Partial`, pra.Partial || 0, orgDataPoint);
          updateDataPoint(`${pra.Name} Deficient`, pra.Deficient || 0, orgDataPoint);

          if (showPracticeCharacterizationForUnit) {
            updateDataPoint(`${pra.Name} Satisfied`, pra.Evaluation == 0 ? 1 : null, unitDataPoint);
            updateDataPoint(`${pra.Name} Partial`, pra.Evaluation == 1 ? 1 : null, unitDataPoint);
            updateDataPoint(`${pra.Name} Deficient`, pra.Evaluation == 2 ? 1 : null, unitDataPoint);
          }
        });
      }

      if (totalAppraisalIndustry < 3) {
        selectedPaIndustry.Practices.forEach(pra => {
          updateDataPoint(`${pra.Name} Satisfied`, `N/A`, industryDataPoint);
          updateDataPoint(`${pra.Name} Partial`, `N/A`, industryDataPoint);
          updateDataPoint(`${pra.Name} Deficient`, `N/A`, industryDataPoint);
        });
      } else {
        selectedPaIndustry.Practices.forEach(pra => {
          updateDataPoint(`${pra.Name} Satisfied`, pra.Satisfied || 0, industryDataPoint);
          updateDataPoint(`${pra.Name} Partial`, pra.Partial || 0, industryDataPoint);
          updateDataPoint(`${pra.Name} Deficient`, pra.Deficient || 0, industryDataPoint);
        });
      }
    }

    // Load Unit vs.
    if (unitPercent) {
      unitDataPoint[`Percent`] = `${unitPercent}%`;
    }

    if (targetPracticeArea && isSelectUnitVs) {
      const orgPercent = selectedPaOrg.AggregatePercentageExact;
      const industryPercent = selectedPaIndustry.AggregatePercentageExact;

      industryDataPoint[`Percent`] = `${orgPercent}%`;
      orgDataPoint[`Percent`] = `${industryPercent}%`;
    }

    // Load Aggregate Percentage
    if (targetPracticeArea && isSelectAggregatePercentage) {
      const unitAggregateRange =
        defaultAggregatePercentageModel()[getPercentageRangeIndex(unitPercent)].range;

      if (totalAppraisalOrg < 3) {
        orgDataPoint[`Percent`] = `N/A`;
        selectedPaOrg.AggregatePercentage.forEach(agg => {
          orgDataPoint[`${agg.Name}`] = `N/A`;
        });
      } else {
        selectedPaOrg.AggregatePercentage.forEach(agg => {
          orgDataPoint[`${agg.Name}`] = `${agg.Results}`;
        });
      }

      if (unitAggregateRange) {
        unitDataPoint[`${unitAggregateRange}`] = 1;
      }

      if (totalAppraisalIndustry < 3) {
        industryDataPoint[`Percent`] = `N/A`;
        selectedPaIndustry.AggregatePercentage.forEach(agg => {
          industryDataPoint[`${agg.Name}`] = `N/A`;
        });
      } else {
        selectedPaIndustry.AggregatePercentage.forEach(agg => {
          industryDataPoint[`${agg.Name}`] = `${agg.Results}`;
        });
      }
    }

    preparedData.push(unitDataPoint);
    preparedData.push(industryDataPoint);
    preparedData.push(orgDataPoint);
  }

  return preparedData;
};

export const getAppraisalReportCsvData = async (
  appraisalOrgUnit,
  appraisalProgramYear,
  orgUnitId,
  selectedReports,
  isPercentageVisible,
  showPracticeCharacterizationForUnit
) => {
  const result = await prepareData(
    appraisalOrgUnit,
    appraisalProgramYear,
    orgUnitId,
    selectedReports,
    isPercentageVisible,
    showPracticeCharacterizationForUnit
  );
  return convertArrayToCSV(result);
};

const updateDataPoint = (key, value, ...dataPoints) => {
  for (const dataPoint of dataPoints) {
    dataPoint[key] = value;
  }
};
